import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  InputAdornment,
  Divider,
  Chip,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BusinessIcon from "@mui/icons-material/Business";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CategoryIcon from "@mui/icons-material/Category";
import ApiIcon from "@mui/icons-material/Api";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import MapIcon from "@mui/icons-material/Map";
import MarkunreadMailboxIcon from "@mui/icons-material/MarkunreadMailbox";

// Contexts and Components
import { useAuth } from "../../contexts/AuthContext";

// Services
import authFetch, { authAPI } from "../../services/api";

// Utils and Constants
import { INDUSTRY_TYPES, DEFAULT_COLORS } from "../../constants/organization";

const STEPS = [
  { label: "Company Info", icon: BusinessIcon },
  { label: "Contact Details", icon: PhoneIcon },
  { label: "Industry", icon: CategoryIcon },
  { label: "Brand Colors", icon: ColorLensIcon },
  { label: "Custom API", icon: ApiIcon },
];

const AddOrganizationWizard = ({ open, onClose }) => {
  const { setUser } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    companyName: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    industry: "",
    primaryColor: "#1976D2",
    secondaryColor: "#E3F2FD",
    textColor: "#FFFFFF",
    apiEndpoint: "",
    apiKey: "",
    apiTestStatus: null,
  });

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleColorPresetSelect = (preset) => {
    setFormData((prev) => ({
      ...prev,
      primaryColor: preset.primary,
      secondaryColor: preset.secondary,
      textColor: preset.text,
    }));
  };

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleTestApi = () => {
    // Simulate API test
    setFormData((prev) => ({ ...prev, apiTestStatus: "testing" }));
    setTimeout(() => {
      // Mock: randomly succeed or fail for demo
      const success = Math.random() > 0.3;
      setFormData((prev) => ({
        ...prev,
        apiTestStatus: success ? "success" : "error",
      }));
    }, 1500);
  };

  const handleClose = () => {
    setActiveStep(0);
    setError("");
    setFormData({
      companyName: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      industry: "",
      primaryColor: "#1976D2",
      secondaryColor: "#E3F2FD",
      textColor: "#FFFFFF",
      apiEndpoint: "",
      apiKey: "",
      apiTestStatus: null,
    });
    onClose();
  };

  const handleSubmit = async () => {
    setError("");
    setIsLoading(true);

    try {
      const response = await authFetch("/companies/", {
        method: "POST",
        body: JSON.stringify({
          name: formData.companyName,
          industry: formData.industry,
          phone: formData.phone || null,
          address: formData.address || null,
          city: formData.city || null,
          state: formData.state || null,
          zipCode: formData.zipCode || null,
          primaryColor: formData.primaryColor,
          secondaryColor: formData.secondaryColor,
          textColor: formData.textColor,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error ||
          errorData.detail ||
          "Failed to create organization",
        );
      }

      await response.json();

      // Refresh user data so the new company appears in UI
      try {
        const updatedUser = await authAPI.getMe();
        setUser(updatedUser);
      } catch (fetchErr) {
        console.error(
          "Failed to refresh user after creating company",
          fetchErr,
        );
      }

      handleClose();
    } catch (err) {
      setError(
        err.message || "Failed to create organization. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return formData.companyName.trim().length > 0;
      case 1:
        return true; // Optional fields
      case 2:
        return formData.industry.length > 0;
      case 3:
        return true; // Colors have defaults
      case 4:
        return true; // API is optional
      default:
        return true;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter the official name of your organization.
            </Typography>
            <TextField
              fullWidth
              label="Company Name"
              placeholder="e.g., Century 21 - Abrams & Associates"
              value={formData.companyName}
              onChange={handleInputChange("companyName")}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        );

      case 1:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add contact information for your organization (optional).
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  placeholder="+1 (555) 123-4567"
                  value={formData.phone}
                  onChange={handleInputChange("phone")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Street Address"
                  placeholder="123 Main Street"
                  value={formData.address}
                  onChange={handleInputChange("address")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="City"
                  placeholder="Newark"
                  value={formData.city}
                  onChange={handleInputChange("city")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationCityIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="State"
                  placeholder="NJ"
                  value={formData.state}
                  onChange={handleInputChange("state")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MapIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  placeholder="07102"
                  value={formData.zipCode}
                  onChange={handleInputChange("zipCode")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MarkunreadMailboxIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select the industry that best describes your organization.
            </Typography>
            <FormControl fullWidth required>
              <InputLabel>Industry Type</InputLabel>
              <Select
                value={formData.industry}
                onChange={handleInputChange("industry")}
                label="Industry Type"
                startAdornment={
                  <InputAdornment position="start">
                    <CategoryIcon color="action" />
                  </InputAdornment>
                }
              >
                {INDUSTRY_TYPES.map((industry) => (
                  <MenuItem key={industry} value={industry}>
                    {industry}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose brand colors for your organization. Select a preset or
              customize.
            </Typography>

            {/* Color Presets */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: "block" }}
            >
              Color Presets
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
              {DEFAULT_COLORS.map((preset) => (
                <Chip
                  key={preset.name}
                  label={preset.name}
                  onClick={() => handleColorPresetSelect(preset)}
                  sx={{
                    backgroundColor: preset.primary,
                    color: preset.text,
                    "&:hover": { backgroundColor: preset.primary, opacity: 0.9 },
                  }}
                />
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Custom Colors */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 2, display: "block" }}
            >
              Custom Colors
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <input
                    type="color"
                    value={formData.primaryColor}
                    onChange={handleInputChange("primaryColor")}
                    style={{
                      width: 40,
                      height: 40,
                      border: "none",
                      cursor: "pointer",
                    }}
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Primary
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formData.primaryColor}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <input
                    type="color"
                    value={formData.secondaryColor}
                    onChange={handleInputChange("secondaryColor")}
                    style={{
                      width: 40,
                      height: 40,
                      border: "none",
                      cursor: "pointer",
                    }}
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Secondary
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formData.secondaryColor}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <input
                    type="color"
                    value={formData.textColor}
                    onChange={handleInputChange("textColor")}
                    style={{
                      width: 40,
                      height: 40,
                      border: "none",
                      cursor: "pointer",
                    }}
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Text
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formData.textColor}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Preview */}
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: "block" }}
              >
                Preview
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 260,
                  height: 36,
                  px: 2,
                  borderRadius: 1.5,
                  border: "2px solid",
                  borderColor: formData.primaryColor,
                  backgroundColor: formData.secondaryColor,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: formData.textColor }}
                >
                  {formData.companyName || "Company Name"}
                </Typography>
              </Box>
            </Box>
          </Box>
        );

      case 4:
        return (
          <Box sx={{ py: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={500}>
                Coming Soon
              </Typography>
              <Typography variant="caption">
                Custom API integration allows you to connect your own data
                endpoints. Configure your RESTful API endpoint below.
              </Typography>
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Provide your API endpoint and credentials. We will send requests
              to your server and expect a specific JSON response format.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="API Endpoint URL"
                  placeholder="https://api.yourcompany.com/v1/data"
                  value={formData.apiEndpoint}
                  onChange={handleInputChange("apiEndpoint")}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ApiIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Your RESTful API endpoint"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="API Key / Token"
                  placeholder="sk-xxxxxxxxxxxxxxxx"
                  type="password"
                  value={formData.apiKey}
                  onChange={handleInputChange("apiKey")}
                  disabled
                  helperText="Authentication credentials for your API"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 2, display: "block" }}
            >
              Expected Response Format
            </Typography>
            <Box
              sx={{
                p: 2,
                backgroundColor: "grey.100",
                borderRadius: 1,
                fontFamily: "monospace",
                fontSize: 12,
                overflow: "auto",
              }}
            >
              <pre style={{ margin: 0 }}>
                {`{
  "success": true,
  "data": {
    "projects": [...],
    "tasks": [...],
    "reports": [...]
  }
}`}
              </pre>
            </Box>

            <Box sx={{ mt: 3, display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleTestApi}
                disabled
                startIcon={<ApiIcon />}
              >
                Test API
              </Button>
              {formData.apiTestStatus === "testing" && (
                <Typography variant="body2" color="text.secondary">
                  Testing connection...
                </Typography>
              )}
              {formData.apiTestStatus === "success" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "success.main",
                  }}
                >
                  <CheckCircleIcon fontSize="small" />
                  <Typography variant="body2">Connection successful</Typography>
                </Box>
              )}
              {formData.apiTestStatus === "error" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "error.main",
                  }}
                >
                  <ErrorIcon fontSize="small" />
                  <Typography variant="body2">Connection failed</Typography>
                </Box>
              )}
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ pb: 1, pr: 6, bgcolor: 'primary.main', color: 'secondary.contrastText' }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <BusinessIcon color="inherit" />
          <Typography variant="h6" color="inherit" fontWeight={600}>
            Add New Organization
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8, color: 'secondary.contrastText' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3, pt: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((step) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ pt: 2, minHeight: 300 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={handleBack} disabled={activeStep === 0 || isLoading}>
          Back
        </Button>
        <Box sx={{ flex: 1 }} />
        {activeStep === STEPS.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!isStepValid() || isLoading}
            startIcon={
              isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : undefined
            }
          >
            {isLoading ? "Creating..." : "Create Organization"}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!isStepValid()}
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddOrganizationWizard;
